<template>
    <!-- Botón "Añadir o quitar características" (solo admin) en la pestaña
         Características. Abre ahí mismo la lista completa de Comodidades y
         Cercano a con casillas; al guardar pregunta el alcance. -->
    <div v-if="isAdmin" class="hf-feat">
        <button v-if="!editando" type="button" class="hf-feat__open" @click="abrir">
            <i class="fas fa-plus-circle" aria-hidden="true" /> {{ $t('admin_tools.edit.features') }}
        </button>

        <form v-else class="hf-feat__form" @submit.prevent="guardar">
            <p class="hf-feat__hint">{{ $t('admin_tools.edit.features_hint') }}</p>

            <fieldset v-for="g in grupos" :key="g.key" class="hf-feat__group">
                <legend>{{ g.titulo }}</legend>
                <div class="hf-feat__grid">
                    <label
                        v-for="item in g.items"
                        :key="item.col"
                        :class="['hf-feat__item', { 'hf-feat__item--on': marcadas[item.col], 'hf-feat__item--changed': marcadas[item.col] !== inicial[item.col] }]"
                    >
                        <input v-model="marcadas[item.col]" type="checkbox" />
                        <img :src="require(`@/assets/alojamiento/${item.icon}`)" alt="" class="hf-feat__icon" />
                        <span>{{ $t(item.text) }}</span>
                    </label>
                </div>
            </fieldset>

            <div class="hf-feat__actions">
                <button type="button" class="hf-feat__btn" :disabled="guardando" @click="editando = false">{{ $t('admin_tools.edit.cancel') }}</button>
                <button type="submit" class="hf-feat__btn hf-feat__btn--primary" :disabled="guardando">
                    <b-spinner v-if="guardando" small />
                    {{ $t(guardando ? 'admin_tools.edit.saving' : 'admin_tools.edit.save') }}
                </button>
            </div>
        </form>

        <AdminAlcanceModal ref="alcance" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import houseData from '@/components/DetalleAlojamiento/data/house-data.json';
import locationData from '@/components/DetalleAlojamiento/data/location-data.json';
import { columnaCaracteristica } from '@/Utils/textoIdiomas';
import AdminAlcanceModal from './AdminAlcanceModal';
import guardarFicha, { aviso } from './guardarFicha';

// `maximo_huespedes` está en la lista de comodidades pero es un número, no una
// característica que se pone o se quita.
const NO_EDITABLES = ['maximo_huespedes'];

const aItems = lista => lista
    .filter(i => NO_EDITABLES.indexOf(i.field) === -1)
    .map(i => ({ ...i, col: columnaCaracteristica(i.field) }));

export default {
    name: 'AdminCaracteristicasEditable',
    components: { AdminAlcanceModal },
    props: {
        accommodation: { type: Object, required: true },
        tituloComodidades: { type: String, default: 'Comodidades del alojamiento' },
        tituloCercano: { type: String, default: 'Cercano a' },
    },
    data() {
        return { editando: false, guardando: false, marcadas: {}, inicial: {} };
    },
    computed: {
        ...mapGetters(['isAdmin']),
        grupos() {
            return [
                { key: 'house', titulo: this.tituloComodidades, items: aItems(houseData) },
                { key: 'near', titulo: this.tituloCercano, items: aItems(locationData) },
            ];
        },
    },
    watch: {
        'accommodation.idalojamiento'() { this.editando = false; },
    },
    methods: {
        abrir() {
            const m = {};
            this.grupos.forEach(g => g.items.forEach(i => { m[i.col] = Number(this.accommodation[i.col]) > 0; }));
            this.marcadas = m;
            this.inicial = { ...m };
            this.editando = true;
        },
        async guardar() {
            const cambios = {};
            Object.keys(this.marcadas).forEach(c => { if (this.marcadas[c] !== this.inicial[c]) cambios[c] = this.marcadas[c]; });
            if (!Object.keys(cambios).length) {
                aviso(this, this.$t('admin_tools.toast.no_changes'), 'info');
                return;
            }
            this.guardando = true;
            const ok = await guardarFicha(this, { caracteristicas: cambios });
            this.guardando = false;
            if (ok) this.editando = false;
        },
    },
};
</script>

<style scoped lang="scss">
.hf-feat {
    margin: 4px 0 18px;

    &__open {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 7px 14px;
        border: 1.5px dashed $hf-sec-pine;
        border-radius: 8px;
        background: #fff;
        font-size: .88rem;
        font-weight: 700;
        color: $hf-sec-pine;
        cursor: pointer;
        &:hover { background: #F4F8F6; }
        &:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }
    }
    &__form { padding: 14px; border: 1.5px solid $grey-10; border-radius: 10px; background: #FAFBFA; }
    &__hint { margin: 0 0 10px; font-size: .85rem; color: #6B7280; }
    &__group {
        margin: 0 0 14px;
        padding: 0;
        border: 0;
        legend { margin-bottom: 8px; font-size: .95rem; font-weight: 700; color: #111827; }
    }
    &__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 6px; }
    &__item {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        padding: 6px 8px;
        border: 1.5px solid $grey-10;
        border-radius: 8px;
        background: #fff;
        font-size: .86rem;
        cursor: pointer;
        input { accent-color: $hf-sec-pine; width: 16px; height: 16px; flex-shrink: 0; }
        &--on { border-color: $hf-sec-pine; }
        &--changed { box-shadow: 0 0 0 2px #FDE68A; }
    }
    &__icon { width: 20px; height: 20px; flex-shrink: 0; }
    &__actions { display: flex; justify-content: flex-end; gap: 8px; }
    &__btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 7px 14px;
        border: 1.5px solid $grey-10;
        border-radius: 8px;
        background: #fff;
        font-weight: 700;
        color: #374151;
        cursor: pointer;
        &:disabled { opacity: .55; cursor: not-allowed; }
        &--primary { background: $hf-sec-pine; border-color: $hf-sec-pine; color: #fff; }
    }
}
</style>
